import axios from "axios";
import SetupAuth from "./configAuth.js";

/**
 * Cliente do Higher-API.
 *
 * As credenciais compartilhadas (X-Tenant, APIKEY, ServiceAccountName,
 * x-forwarded-for e o Basic do swap) NÃO moram mais aqui: elas estavam
 * hardcoded neste arquivo, iam para o bundle e apareciam inteiras no console do
 * navegador. Agora o nginx que serve o SPA injeta esses cabeçalhos no caminho
 * `/proxy/...` (ver `app/nginx.conf.template`).
 *
 * O que continua no navegador é só o Bearer do próprio usuário, guardado em
 * sessionStorage — esse é dele por definição, e esconder não faria sentido.
 */

/** Caminho que injeta tenant + apikey e repassa o Bearer do usuário. */
const BACKEND_PROXY = "/proxy/backend";
/** Caminho que injeta, além disso, o Basic do swap (trade na Foxbit). */
const SWAP_PROXY = "/proxy/swap";

/**
 * @param {{ useSwapCredentials?: boolean }} options
 *   `useSwapCredentials` troca o caminho para o proxy que injeta o Basic do
 *   swap. Antes disso era o chamador que passava o Basic em texto puro, que é
 *   justamente o que precisava sair do navegador.
 */
function SetupBackend(options = {}) {
  const useSwapCredentials = Boolean(options.useSwapCredentials);

  const setup = axios.create({
    baseURL: useSwapCredentials ? SWAP_PROXY : BACKEND_PROXY,
  });

  function isPublicRoute() {
    const currentPath = window.location.pathname;
    return [
      "/login",
      "/register",
      "/registerPF",
      "/register-verification",
    ].includes(currentPath);
  }

  function isTokenExpired() {
    const tokenTimestamp = sessionStorage.getItem("tokenTimestamp");
    if (!tokenTimestamp) return true;

    const expirationTime = 60 * 60 * 1000;
    const tokenAge = Date.now() - tokenTimestamp;

    return tokenAge > expirationTime;
  }

  async function renewToken() {
    try {
      const response = await SetupAuth.post("/v1/authentication/login");

      if (response.status !== 200) {
        throw new Error("Login failed");
      }

      const data = response.data;
      sessionStorage.setItem("token", `Bearer ${data}`);
      sessionStorage.setItem("tokenTimestamp", Date.now());
    } catch (error) {
      console.error("Erro ao renovar o token:", error);
      if (!isPublicRoute()) {
        window.location.href = "/login";
      }
    }
  }

  setup.interceptors.request.use(
    async (config) => {
      // Chamada com credencial de swap não usa o token do usuário, então não
      // faz sentido renovar o token por causa dela.
      if (!useSwapCredentials && isTokenExpired()) {
        await renewToken();
      }

      if (!useSwapCredentials) {
        config.headers["Authorization"] = sessionStorage.getItem("token");
      }

      const baasBearerToken = sessionStorage.getItem("baasBearerToken");
      if (baasBearerToken) {
        config.headers["X-BaaS-Authorization"] = `Bearer ${baasBearerToken}`;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  return setup;
}

export default SetupBackend;
